import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Divider,
  Grid,
  Stack,
  IconButton,
  Tooltip,
  LinearProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ErrorOutline as ErrorIcon,
  MoreHoriz as MoreHorizIcon,
  AccountBalanceWallet as WalletIcon,
  Refresh as RefreshIcon,
  RequestQuote as RequestQuoteIcon,
  ArrowForward as ArrowForwardIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import financialRequestService from '../../../services/financialRequestService';

// Helper function to get status color
const getStatusColor = (status) => {
  switch (status) {
    case 'Requested':
      return 'info';
    case 'Review':
      return 'warning';
    case 'Approved':
      return 'success';
    case 'Declined':
      return 'error';
    case 'MemberRejected':
      return 'error';
    case 'Disbursed':
      return 'success';
    case 'Completed':
      return 'success';
    default:
      return 'default';
  }
};

// Helper function to format dates
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Helper to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount);
};

const MemberFinancialRequestsPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({
    total: 0,
    inProgress: 0,
    completed: 0,
    denied: 0
  });

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await financialRequestService.getMemberRequests();
      setRequests(response.data || []);
      
      // Calculate counts for different statuses
      if (response.data && response.data.length > 0) {
        const total = response.data.length;
        const inProgress = response.data.filter(r => 
          ['Requested', 'Review', 'Approved', 'Disbursed'].includes(r.status)
        ).length;
        const completed = response.data.filter(r => r.status === 'Completed').length;
        const denied = response.data.filter(r => 
          ['Declined', 'MemberRejected'].includes(r.status)
        ).length;
        
        setCounts({ total, inProgress, completed, denied });
      }
    } catch (err) {
      console.error('Error fetching financial requests:', err);
      setError(err.message || 'Error loading your financial requests. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchRequests();
    } catch (err) {
      console.error('Error refreshing data:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleCreateRequest = () => {
    navigate('/member/financial-requests/create');
  };

  const handleViewRequest = (requestId) => {
    navigate(`/member/financial-requests/${requestId}`);
  };

  // Status display mapping
  const statusDisplay = {
    'Requested': 'Requested',
    'Review': 'In Review',
    'Approved': 'Approved',
    'Declined': 'Declined',
    'MemberRejected': 'Rejected by You',
    'Disbursed': 'Funds Disbursed',
    'Completed': 'Completed'
  };

  // Status icon mapping
  const StatusIcon = ({ status }) => {
    switch (status) {
      case 'Requested':
        return <ScheduleIcon fontSize="small" color="info" />;
      case 'Review':
        return <MoreHorizIcon fontSize="small" color="warning" />;
      case 'Approved':
        return <CheckCircleIcon fontSize="small" color="success" />;
      case 'Disbursed':
        return <WalletIcon fontSize="small" color="success" />;
      case 'Completed':
        return <CheckCircleIcon fontSize="small" color="success" />;
      case 'Declined':
        return <ErrorIcon fontSize="small" color="error" />;
      case 'MemberRejected':
        return <ErrorIcon fontSize="small" color="error" />;
      default:
        return <InfoIcon fontSize="small" />;
    }
  };

  const renderSummaryCards = () => (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ 
          borderRadius: 2, 
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          height: '100%'
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ 
                p: 1, 
                borderRadius: '50%', 
                bgcolor: 'primary.light',
                display: 'flex'
              }}>
                <RequestQuoteIcon color="primary" />
              </Box>
              <Typography variant="subtitle2" sx={{ ml: 1, color: 'text.secondary' }}>
                TOTAL REQUESTS
              </Typography>
            </Box>
            <Typography variant="h3" fontWeight="bold">
              {counts.total}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              All financial assistance requests
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ 
          borderRadius: 2, 
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          height: '100%',
          background: 'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)',
          color: 'white'
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ 
                p: 1, 
                borderRadius: '50%', 
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex'
              }}>
                <MoreHorizIcon />
              </Box>
              <Typography variant="subtitle2" sx={{ ml: 1, color: 'rgba(255,255,255,0.8)' }}>
                IN PROGRESS
              </Typography>
            </Box>
            <Typography variant="h3" fontWeight="bold">
              {counts.inProgress}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255,255,255,0.8)' }}>
              Awaiting review or disbursement
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ 
          borderRadius: 2, 
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          height: '100%',
          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
          color: 'white'
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ 
                p: 1, 
                borderRadius: '50%', 
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex'
              }}>
                <CheckCircleIcon />
              </Box>
              <Typography variant="subtitle2" sx={{ ml: 1, color: 'rgba(255,255,255,0.8)' }}>
                COMPLETED
              </Typography>
            </Box>
            <Typography variant="h3" fontWeight="bold">
              {counts.completed}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255,255,255,0.8)' }}>
              Successfully processed requests
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ 
          borderRadius: 2, 
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          height: '100%',
          background: 'linear-gradient(135deg, #e44d26 0%, #f16529 100%)',
          color: 'white'
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ 
                p: 1, 
                borderRadius: '50%', 
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex'
              }}>
                <ErrorIcon />
              </Box>
              <Typography variant="subtitle2" sx={{ ml: 1, color: 'rgba(255,255,255,0.8)' }}>
                DENIED/REJECTED
              </Typography>
            </Box>
            <Typography variant="h3" fontWeight="bold">
              {counts.denied}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255,255,255,0.8)' }}>
              Declined or rejected requests
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ py: 3, px: { xs: 2, md: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <RequestQuoteIcon sx={{ fontSize: 28, mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h5" component="h1" fontWeight="bold">
            Financial Assistance Requests
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Tooltip title="Refresh data">
            <IconButton 
              onClick={handleRefresh} 
              disabled={refreshing}
              sx={{ 
                bgcolor: 'background.paper', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateRequest}
            sx={{ 
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              px: 3,
              borderRadius: '8px'
            }}
          >
            New Request
          </Button>
        </Box>
      </Box>

      {refreshing && (
        <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {!loading && !error && requests.length > 0 && renderSummaryCards()}

      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          py: 8,
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2
        }}>
          <CircularProgress size={40} />
          <Typography color="text.secondary">
            Loading your financial requests...
          </Typography>
        </Box>
      ) : error ? null : requests.length === 0 ? (
        <Card sx={{ 
          p: 6, 
          textAlign: 'center',
          borderRadius: 3,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e7eb 100%)'
        }}>
          <Box sx={{ mb: 3 }}>
            <RequestQuoteIcon sx={{ fontSize: 60, color: 'primary.main', opacity: 0.7 }} />
          </Box>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 'medium' }}>
            No financial assistance requests yet
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Create your first request to get financial assistance for medical, educational, or other qualified needs.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<AddIcon />}
            onClick={handleCreateRequest}
            sx={{ 
              mt: 2, 
              borderRadius: '8px',
              px: 4,
              py: 1.5,
              boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
              background: 'linear-gradient(90deg, #1976d2, #1565c0)'
            }}
          >
            Create Your First Request
          </Button>
        </Card>
      ) : (
        <Card sx={{ 
          borderRadius: 3, 
          overflow: 'hidden',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
        }}>
          <CardContent sx={{ p: 0 }}>
            {isMobile ? (
              // Mobile card view
              <Box sx={{ p: 2 }}>
                <Stack spacing={2}>
                  {requests.map((request) => (
                    <Card 
                      key={request._id}
                      variant="outlined"
                      sx={{ 
                        p: 2, 
                        borderRadius: 2,
                        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 6px 12px rgba(0,0,0,0.1)',
                          cursor: 'pointer'
                        }
                      }}
                      onClick={() => handleViewRequest(request._id)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {request.reason}
                        </Typography>
                        <Chip 
                          icon={<StatusIcon status={request.status} />}
                          label={statusDisplay[request.status] || request.status} 
                          color={getStatusColor(request.status)}
                          size="small"
                          sx={{ fontWeight: 'medium' }}
                        />
                      </Box>
                      
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Date
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(request.requestDate)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Amount
                          </Typography>
                          <Typography variant="body2">
                            {request.amount > 0 
                              ? formatCurrency(request.amount) 
                              : 'Not specified'}
                          </Typography>
                        </Grid>
                      </Grid>
                      
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'flex-end', 
                        alignItems: 'center',
                        mt: 2
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                          Updated {formatDate(request.lastUpdated)}
                        </Typography>
                        <ArrowForwardIcon fontSize="small" color="primary" />
                      </Box>
                    </Card>
                  ))}
                </Stack>
              </Box>
            ) : (
              // Desktop table view
              <TableContainer>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'background.paperDark' }}>
                      <TableCell>Request Date</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Last Updated</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requests.map((request) => (
                      <TableRow 
                        key={request._id} 
                        hover
                        sx={{
                          transition: 'background-color 0.2s',
                          '&:hover': {
                            bgcolor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                      >
                        <TableCell>{formatDate(request.requestDate)}</TableCell>
                        <TableCell>
                          <Typography fontWeight="medium">
                            {request.reason}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight="medium">
                            {request.amount > 0 
                              ? formatCurrency(request.amount) 
                              : 'Not specified'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            icon={<StatusIcon status={request.status} />}
                            label={statusDisplay[request.status] || request.status} 
                            color={getStatusColor(request.status)} 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>{formatDate(request.lastUpdated)}</TableCell>
                        <TableCell align="right">
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleViewRequest(request._id)}
                            sx={{ 
                              boxShadow: 'none',
                              borderRadius: '8px'
                            }}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default MemberFinancialRequestsPage;